import { firebaseAuth } from "../../config/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  deleteUser,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import axios from "axios";

const API_BASE_URL =
  import.meta.env.VITE_BASE_URL_API_BACKEND ||
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:4000/api/v1";

export default class AuthService {
  static createUserFirebase = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );

      return {
        message: userCredential.user,
        type: "success",
      };
    } catch (error) {
      switch (error.code) {
        case "auth/email-already-in-use":
          throw new Error("Esse e-mail já possui conta cadastrada");
        case "auth/weak-password":
          throw new Error("A senha inserida é muito fraca... Tente novamente!");
        case "auth/internal-error":
          throw new Error(
            "Erro inesperado ao tentar processar a autenticação. Tente novamente ou contate o suporte",
          );
        case "auth/network-request-failed":
          throw new Error(
            "Verifique o seu acesso a internet e tente novamente",
          );
        default:
          throw new Error("Verifique os dados e tente novamente...");
      }
    }
  };

  static registerUser = async (userData) => {
    const { email, password, name, lastName, role, phone, code } = userData;
    const emailFormatted = email.trim();
    const passwordFormatted = password.trim();
    let firebaseUser = null;

    const { message, type } = await this.createUserFirebase(
      emailFormatted,
      passwordFormatted,
    );

    if (type === "success") {
      firebaseUser = message;

      const fullName = [name, lastName].filter(Boolean).join(" ");
      if (fullName) {
        try {
          await updateProfile(firebaseUser, { displayName: fullName });
        } catch {
          // Não bloqueia o cadastro se a atualização do displayName falhar
        }
      }

      const userDataToDB = {
        firebase_uid: firebaseUser.uid,
        email,
        name,
        lastName,
        role,
        phone,
        code,
      };

      try {
        await axios.post(
          `${import.meta.env.VITE_BASE_URL_API_BACKEND}/auth/register`,
          userDataToDB,
        );

        await this.sendEmailVerificationFirebase();

        return {
          message: "Cadastro concluído com sucesso",
          type: "success",
        };
      } catch (error) {
        if (firebaseUser) {
          try {
            await deleteUser(firebaseUser);
          } catch (error) {
            throw new Error("Erro ao deletar usuário do Firebase");
          }
        }

        throw new Error(
          error.response?.data.error.message ?? "Erro ao cadastrar usuário",
        );
      }
    } else {
      throw new Error(message);
    }
  };

  static signInWithEmail = async (userEmail, password) => {
    const emailFormatted = userEmail.trim();
    const passwordFormatted = password.trim();

    try {
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        emailFormatted,
        passwordFormatted,
      );

      const token = await userCredential.user.getIdToken();
      localStorage.setItem("auth_token", token);

      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = response.data?.data ?? response.data;
      const tenantId =
        user?.fk_id_company || user?.company?.id || user?.company?.fk_id_company;
      if (tenantId) {
        localStorage.setItem("tenant_id", String(tenantId));
      } else {
        console.warn("AuthService: não encontrou tenant_id em /auth/me", user);
      }
      const companyName = user?.company?.name;
      if (companyName) {
        localStorage.setItem("company_name", companyName);
      }

      return {
        message: "Login efetuado com sucesso!",
        type: "success",
      };
    } catch (error) {
      if (error.response?.status === 401) {
        await this.signOut();
        throw new Error(
          error.response?.data?.error?.message ?? "Erro ao se autenticar",
        );
      }

      switch (error.code) {
        case "auth/user-not-found":
          throw new Error("O e-mail informado não possui conta cadastrada");
        case "auth/wrong-password":
          throw new Error(
            "A senha informada está incorreta. Tente novamente...",
          );
        case "auth/internal-error":
          throw new Error(
            "Erro inesperado ao tentar processar a autenticação. Tente novamente ou contate o suporte",
          );
        case "auth/network-request-failed":
          throw new Error(
            "Verifique o seu acesso a internet e tente novamente",
          );
        default:
          throw new Error(
            "Verifique se os campos foram preenchidos corretamente",
          );
      }
    }
  };

  static signOut = async () => {
    try {
      await signOut(firebaseAuth);
      localStorage.removeItem("auth_token");
      localStorage.removeItem("tenant_id");
      return {
        message: "Usuário deslogado com sucesso",
        type: "success",
      };
    } catch (error) {
      return {
        message: "Erro ao tentar se deslogar...Tente novamente",
        type: "error",
      };
    }
  };

  static resetPassword = async (userEmail) => {
    let emailFormatted = userEmail.trim();

    try {
      await sendPasswordResetEmail(firebaseAuth, emailFormatted);

      return {
        message: "E-mail para redefinição de senha enviado com sucesso!",
        type: "success",
      };
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        throw new Error("O e-mail informado não possui conta cadastrada");
      }
      throw new Error("Não foi possível concluir a solicitação");
    }
  };

  static sendEmailVerificationFirebase = async () => {
    try {
      const user = firebaseAuth.currentUser;

      if (!user) {
        throw new Error("Usuário não está logado");
      }

      await sendEmailVerification(user);

      return {
        message: "E-mail enviado com sucesso!",
        type: "success",
      };
    } catch (error) {
      switch (error.code) {
        case "auth/too-many-requests":
          return {
            message: "Limite de envio atingido. Aguarde um momento.",
            type: "error",
          };
        default:
          return {
            message: "Erro ao enviar e-mail de validação. Tente novamente!",
            type: "error",
          };
      }
    }
  };

  static markEmailAsVerified = async (firebase_uid) => {
    try {
      await axios.post(`${API_BASE_URL}/auth/verify-email`, firebase_uid);
      return {
        message: "Campo email_verified atualizado com sucesso!",
        type: "success",
      };
    } catch (error) {
      return {
        message: "Erro ao atualizar campo email_verified",
      };
    }
  };
}
